<script setup lang="ts">
import type { ContentBlock } from '@nimbus/types'
import {
    useContentMedia,
    useContentSeo,
    useRelativeTime,
    useDayjs,
    useHead,
    useSeoMeta,
    computed,
    useRoute,
    useSiteUrl,
    useArticleSchema,
    toSchemaDate,
    toDateModified,
    navigateTo,
    useCoreConfig,
    useNimbusConfig,
} from '#imports'
import { resolveMediaUrl, isLocalMedia } from '@nimbus/utils/resolveMediaUrl'
import { contentBlocksToExcerpt } from '@nimbus/utils/contentBlocksToExcerpt'

const props = defineProps<{
    text: {
        back: string
        otherNews: string
    }
    noPostsText: string
    article?: any
    articles?: any[]
}>()

const { press: config } = useNimbusConfig()

const route = useRoute()
const slug = computed(() => {
    const slug: string | Array<string> | undefined = route?.params?.slug

    if (!slug?.length || Array.isArray(slug)) return ''

    return slug
})

const { locale, site } = useCoreConfig()
const { getContentMediaUrl } = useContentMedia()
const post = computed(() => props.article ?? null)

const relatedPosts = computed(() => {
    if (props.articles?.length) {
        return props.articles.filter(a => a.slug !== slug.value).slice(0, 5)
    }
    return (post.value as any)?.relatedPosts || []
})

if (!post.value?.slug) {
    await navigateTo(config.routes.index, { redirectCode: 301 })
}

// SEO
const excerpt = contentBlocksToExcerpt(post.value?.content) || null
const { getHead, getSeoMeta } = useContentSeo(
    post.value?.SEO || {
        metaTitle: post.value?.title ? `${post.value.title} | ${site.name}` : site.name,
        metaDescription: excerpt,
        metaImage: post.value?.media?.url ? { url: post.value.media.url } : null,
    }
)

useHead(getHead())
useSeoMeta(getSeoMeta())
// ./ SEO

const publishedAt = computed(() => post.value?.publishAt?.publishAt as string | undefined)
const publishedDatetime = computed(() => publishedAt.value ? toSchemaDate(publishedAt.value) : '')

const relatedPostsWithDatetime = computed(() =>
    relatedPosts.value.map((related: any) => ({
        ...related,
        datetime: related.publishAt?.publishAt ? toSchemaDate(related.publishAt.publishAt) : '',
    })))

if (post.value?.slug && publishedAt.value) {
    const origin = useSiteUrl()('')
    const mediaUrl = post.value.media?.url ? resolveMediaUrl(post.value.media.url, getContentMediaUrl) : ''

    useArticleSchema().addArticle({
        type: 'NewsArticle',
        headline: post.value.title,
        description: post.value.SEO?.metaDescription || excerpt,
        image: mediaUrl && origin ? new URL(mediaUrl, origin).href : undefined,
        datePublished: publishedDatetime.value,
        dateModified: toDateModified(publishedAt.value, post.value.updatedAt as string | undefined),
    })
}
const dayjs = useDayjs()
const authorAndDate = computed(() => {
    if (!post.value?.author || !post.value?.publishAt?.publishAt) return ''
    const dateStr = dayjs(post.value.publishAt.publishAt).toDate().toLocaleString(locale, { month: 'long', year: 'numeric' })
    const capitalized = dateStr.charAt(0).toUpperCase() + dateStr.slice(1)
    return `${capitalized} Af ${post.value?.author}`
})
</script>

<template>
    <div v-if="post?.slug" class="post-container">
        <div class="post">
            <div class="post-heading-container">
                <div class="post__date">
                    <div class="post__label">
                        {{ post?.label }}
                    </div>

                    <time class="post__created" :datetime="publishedDatetime || undefined">
                        {{ useRelativeTime(post?.publishAt?.publishAt, { once: true }) }}
                    </time>
                </div>

                <NuxtLink :to="config.routes.index">
                    {{ text.back }}
                </NuxtLink>
            </div>

            <div class="post-content">
                <div class="post-content__left">
                    <NuxtImg
                        v-if="post?.media?.url"
                        :src="resolveMediaUrl(post.media.url, getContentMediaUrl)"
                        :provider="isLocalMedia(post.media.url) ? 'cloudflare' : undefined"
                        :preset="isLocalMedia(post.media.url) ? 'local' : undefined"
                        loading="lazy"
                        decoding="async"
                    />

                    <h1 class="post__title">
                        {{ post.title }}
                    </h1>
                    <div v-if="authorAndDate" class="post__author">
                        {{ authorAndDate || '' }}
                    </div>
                    <div class="post__content">
                        <NimbusContentBlockRenderer :content="post.content as ContentBlock[]" />
                    </div>
                    <div class="post__tags">
                        <span v-for="tag in post.tags" :key="tag.id">
                            {{ tag.name }}
                        </span>
                    </div>
                </div>
                <div class="other-news">
                    <div class="other-news__title">
                        {{ text.otherNews }}
                    </div>

                    <div
                        v-for="relatedPost in relatedPostsWithDatetime"
                        :key="relatedPost.slug"
                        class="other-news__link"
                    >
                        <NuxtLink :to="{ ...config.routes.detail, params: { slug: relatedPost.slug } }">
                            <div class="other-news__link--text">
                                {{ relatedPost.title }}
                            </div>
                            <time class="post__created post__created--post" :datetime="relatedPost.datetime || undefined">
                                {{ useRelativeTime(relatedPost.publishAt?.publishAt, { once: true }) }}
                            </time>
                        </NuxtLink>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-else class="post-container">
        {{ noPostsText }}
    </div>
</template>

<style lang="scss" scoped>
.post-container {
    width: 100%;
}

.post-heading-container {
    display: flex;
    justify-content: space-between;
    font-size: $font-size-sm;
}

.post-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 40px;

    &__left {
        display: flex;
        flex-direction: column;
        flex: 2;

        img {
            max-width: 100%;
            margin-bottom: 20px;
        }
    }

    &__right {
        display: flex;
        flex-direction: column;
        flex: 1;
    }
}

.post {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 10px;

    background-color: var(--panel-bg, $panel-color);
    padding: 20px;
    border-radius: $border-radius;

    &__date {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &__title {
        font-family: inherit;
        font-size: $font-size-xl;
        font-weight: $text-bold;
        line-height: inherit;
        color: inherit;
        margin: 0 0 15px;
    }

    &__author {
        font-size: $font-size-xxs;
        font-style: italic;
        margin-bottom: 4px;
    }

    &__label {
        font-size: $font-size-xs;
        font-weight: $text-bold;
        background-color: $blue;
        color: white;
        border-radius: var(--border-radius, $border-radius);
        line-height: 1;
        height: min-content;
        padding: 5px;
    }

    &__thumbnail {
        flex-grow: 1;
        max-width: 500px;

        > img {
            height: 100%;
            width: 100%;
            border-radius: $border-radius;
        }
    }

    &__content {
        font-size: $font-size-sm;
        white-space: pre-wrap;
    }

    &__created {
        display: block;
        font-size: $font-size-xs;
        font-weight: $text-bold;
        color: var(--subtle-text-color, $gray-light);

        &--post {
            color: var(--subtle-text-color, $gray);
            font-weight: $text-regular;
            font-size: $font-size-xs;
        }
    }

    &__tags {
        margin: 10px 0;
        display: flex;
        align-items: flex-start;
        gap: 4px;

        span {
            font-size: $font-size-sm;
            font-weight: $text-bold;
            color: $white;
            background-color: $gray-light;
            border-radius: var(--border-radius, $border-radius);
            line-height: 1;
            height: min-content;
            padding: 4px 6px;
        }
    }
}

.other-news {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 10px;
    min-width: 0;

    &__title {
        font-weight: $text-bold;
    }

    &__link {
        margin-bottom: 15px;
        margin-left: 10px;

        &--text {
            font-size: $font-size-sm;
            font-weight: $text-medium;
            color: var(--on-surface, black);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
}

@media (min-width: $screen-xs) {
    .post {
        &__label {
            font-size: $font-size-sm;
        }

        &__created {
            font-size: $font-size-sm;
        }
    }

    .post-heading-container {
        font-size: $font-size-base;
    }
}

@media (min-width: $screen-md) {
    .post-container {
        width: 66%;
    }
}

@media (min-width: $screen-lg) {
    .post-container {
        width: 75%;
    }

    .post-content {
        flex-direction: row;
    }

    .other-news {
        &__title {
            margin-left: -10px;
        }

        &__link {
            margin-left: 0;
        }
    }

    .other-news--mobile {
        display: none;
    }
}
</style>
